import { supabase } from "@/shared/lib/supabaseClient";
import type { Database } from "@/shared/types/database.types";

/**
 * Camada de dados dos Ambientes (Sprint 3, migration 0025). Ambientes são as
 * salas/espaços reserváveis da igreja; a gestão é feita dentro do módulo
 * Reservas (RLS: admin da plataforma OU gerencia_modulo('reservas')).
 */
export type Ambiente = Database["public"]["Tables"]["ambientes"]["Row"];

export interface DadosAmbiente {
  nome: string;
  descricao?: string | null;
  capacidade?: number | null;
  cor?: string | null;
  ativo: boolean;
}

export const ambientesService = {
  /** Todos os ambientes (tela de gestão). */
  async listar(): Promise<Ambiente[]> {
    const { data, error } = await supabase.from("ambientes").select("*").order("nome");
    if (error) throw new Error("Não foi possível carregar os ambientes.");
    return data ?? [];
  },

  /** Apenas ambientes ativos (dropdowns de Reserva/Evento). */
  async listarAtivos(): Promise<Ambiente[]> {
    const { data, error } = await supabase
      .from("ambientes")
      .select("*")
      .eq("ativo", true)
      .order("nome");
    if (error) throw new Error("Não foi possível carregar os ambientes.");
    return data ?? [];
  },

  async criar(dados: DadosAmbiente): Promise<void> {
    const { error } = await supabase.from("ambientes").insert({
      nome: dados.nome,
      descricao: dados.descricao ?? null,
      capacidade: dados.capacidade ?? null,
      cor: dados.cor ?? null,
      ativo: dados.ativo,
    });
    if (error) {
      if (error.code === "23505") throw new Error("Já existe um ambiente com esse nome.");
      throw new Error("Não foi possível criar o ambiente.");
    }
  },

  async atualizar(id: string, dados: DadosAmbiente): Promise<void> {
    const { error } = await supabase
      .from("ambientes")
      .update({
        nome: dados.nome,
        descricao: dados.descricao ?? null,
        capacidade: dados.capacidade ?? null,
        cor: dados.cor ?? null,
        ativo: dados.ativo,
      })
      .eq("id", id);
    if (error) {
      if (error.code === "23505") throw new Error("Já existe um ambiente com esse nome.");
      throw new Error("Não foi possível atualizar o ambiente.");
    }
  },

  /**
   * Exclui o ambiente. Se já houver reservas/eventos apontando para ele, o
   * banco bloqueia (FK) — nesse caso oriente a desativar em vez de excluir.
   */
  async excluir(id: string): Promise<void> {
    const { error } = await supabase.from("ambientes").delete().eq("id", id);
    if (error) {
      if (error.code === "23503") {
        throw new Error(
          "Este ambiente já tem reservas ou eventos vinculados. Desative-o em vez de excluir.",
        );
      }
      throw new Error("Não foi possível excluir o ambiente.");
    }
  },
};
